import { useCallback, useMemo } from 'react';
import useModuleDomNodeStore from '../lib/useModuleDomNodeStore';
import InlineTextFieldEditor from '../components/TextFieldEditor';
import InlineNumberFieldEditor from '../components/NumberFieldEditor';
// @ts-expect-error not typed
import { makeAsyncComponent } from 'ContentEditorUI/components/shared/makeAsyncUtils';
import { getDeepValue, isNullOrUndefined } from 'ContentUtils/helpers/ObjectHelpers';
import ImageFieldEditor from '../components/ImageFieldEditor';
import ReadOnlyFieldEditor from '../components/ReadOnlyFieldEditor';
import { useDispatch } from 'react-redux';
// @ts-expect-error Not typed
import { setIsInlineEditableFieldFocused } from 'InpageEditorUI/redux/actions/layoutSectionDndActions';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const AsyncInlineRichTextFieldEditor = makeAsyncComponent(() => import( /* webpackChunkName: "InlineRichTextFieldEditor" */'InpageEditorUI/components/RichTextFieldEditor'));
function getIsFieldGroup(field) {
  return Boolean(field.children);
}
const ARRAY_INDEX_REGEX = /\[(\d+)\]$/;
function parseArrayIndexesIntoNewPathKeys(path) {
  const newKeyPath = [];
  path.forEach(key => {
    // Little short circuit to avoid RegEx computation
    if (!key.endsWith(']')) {
      newKeyPath.push(key);
      return;
    }
    const arrayMatch = key.match(ARRAY_INDEX_REGEX);
    if (!arrayMatch) {
      // Shouldn't happen but need to make TS happy
      newKeyPath.push(key);
      return;
    }
    const index = arrayMatch[1];
    const repeatedFieldName = key.substring(0, arrayMatch.index);
    newKeyPath.push(repeatedFieldName);
    const parsedIndex = parseInt(index, 10);
    newKeyPath.push(isNaN(parsedIndex) ? index : parsedIndex);
  });
  return newKeyPath;
}
function findLeafField(fields, parsedPathWithArrayIndexes, index = 0) {
  const currentKey = parsedPathWithArrayIndexes[index];
  const foundField = fields.find(field => field.name === currentKey);
  if (!foundField) {
    return null;
  }
  let shadowedIndex = index;
  const isRepeating = Boolean(foundField.occurrence);
  if (isRepeating && index < parsedPathWithArrayIndexes.length - 1) {
    const nextKey = parsedPathWithArrayIndexes[index + 1];
    // Skipping the index part of the field key path since that's not on the schema
    if (typeof nextKey === 'number') {
      shadowedIndex++;
    }
  }
  const isFieldGroup = getIsFieldGroup(foundField);
  if (shadowedIndex === parsedPathWithArrayIndexes.length - 1) {
    return isFieldGroup ? null : foundField;
  }
  return isFieldGroup ? findLeafField(foundField.children, parsedPathWithArrayIndexes, shadowedIndex + 1) : null;
}
export default function ModuleInlineEditingContainer({
  moduleName,
  onChange,
  fields,
  fieldValues,
  inlineDataSource,
  inlineType,
  handleFocusInlineRichTextWithPlaceholder,
  supportsInlinePlaceholdersAndLabels = false,
  updateMetaData,
  getModuleSchemaById,
  getScpModuleInRichTextByName,
  getSelectedItemId,
  registerInlineFieldRef
}) {
  const moduleDomNode = useModuleDomNodeStore(moduleName);
  const dispatch = useDispatch();
  const onFocusCallback = useCallback(() => {
    if (moduleDomNode) {
      moduleDomNode.classList.add('inline-editing-active');
      dispatch(setIsInlineEditableFieldFocused(true));
    }
  }, [dispatch, moduleDomNode]);
  const onBlurCallback = useCallback(() => {
    if (moduleDomNode) {
      moduleDomNode.classList.remove('inline-editing-active');
      dispatch(setIsInlineEditableFieldFocused(false));
    }
  }, [dispatch, moduleDomNode]);
  const isFieldWithMultipleInstancesSharingSameSchema = Array.isArray(fieldValues) && fieldValues.length > 0;
  const inlineEditableFieldsInModule = useMemo(() => {
    if (!moduleDomNode) {
      return [];
    }
    const allDataHSTokensInModule = moduleDomNode.querySelectorAll(`[data-hs-token^="${inlineType}.${inlineDataSource}"]`);
    const result = [];
    allDataHSTokensInModule.forEach((element, idx) => {
      if (!(element instanceof HTMLElement)) {
        console.warn(`Inline instance editing expects DOM selectors to return instance of HTMLElement! Found a non-HTMLElement DOM node of ${element}`);
        return;
      }

      // TS doesn't know that it has the attribute
      const token = element.getAttribute('data-hs-token');
      const [type, source, ...path] = token.split('.');
      if (!type || !source || !path || path.length === 0) {
        console.warn(`Malformed data-hs-token for ${element.getAttribute('data-hs-token')}`);
        return;
      }
      const parsedPath = parseArrayIndexesIntoNewPathKeys(path);
      const shouldIgnoreFirstIndexLookup = isFieldWithMultipleInstancesSharingSameSchema && !isNaN(parseInt(parsedPath[0], 10));

      // Validate that path exists on some field (using schemaPath)
      const potentialField = findLeafField(fields, shouldIgnoreFirstIndexLookup ? parsedPath.slice(1) : parsedPath);
      const inlineEditorKey = `${token}-${idx}`;
      if (potentialField) {
        if (potentialField.type === 'richtext') {
          // Need to mutate the DOM element's `id` because TinyMCE requires unique
          // element id's for unique TinyMCE instances but the renderer doesn't provide
          // us with that. Mutating the `id` directly here was the simplest way to get
          // something up and running quickly
          // Maybe validate that the id isn't unique first
          // to prevent runaway id creation?
          const elementId = element.getAttribute('id');
          if (elementId) {
            element.id = `${elementId}-${idx}`;
          } else {
            element.id = inlineEditorKey;
          }
        }
        result.push({
          field: potentialField,
          path: parsedPath,
          domNode: element,
          inlineEditorKey
        });
      }
    });
    return result;
  }, [moduleDomNode, fields, inlineType, inlineDataSource, isFieldWithMultipleInstancesSharingSameSchema]);
  return /*#__PURE__*/_jsx(_Fragment, {
    children: inlineEditableFieldsInModule.map(({
      field,
      domNode,
      path,
      inlineEditorKey
    }) => {
      const {
        type,
        name,
        label,
        inline_help_text,
        required,
        enabled_features
      } = field;

      // TODO: See if we can do this inside of the editor component itself to bring component
      // updating/rendering further down the tree
      //
      let value = getDeepValue(fieldValues, path);
      if (value === undefined && !isNullOrUndefined(field.default)) {
        value = field.default;
      }
      switch (type) {
        case 'text':
          {
            return /*#__PURE__*/_jsx(InlineTextFieldEditor, {
              node: domNode,
              name: name,
              currentValue: value,
              onUpdate: onChange,
              keyPath: path,
              label: label,
              helpText: inline_help_text,
              isRequired: required,
              requiredForSaving: supportsInlinePlaceholdersAndLabels && field.required_for_saving
              // `label`, `helpText`, and `isRequired` above will only be used
              //  if `supportsInlinePlaceholdersAndLabels` is true
              ,
              supportsInlinePlaceholdersAndLabels: supportsInlinePlaceholdersAndLabels,
              moduleName: moduleName,
              onFocusCallback: onFocusCallback,
              onBlurCallback: onBlurCallback,
              registerInlineFieldRef: registerInlineFieldRef
              // TODO
              // schema={schema}
            }, inlineEditorKey);
          }
        case 'number':
          {
            return /*#__PURE__*/_jsx(InlineNumberFieldEditor, {
              node: domNode,
              name: name,
              currentValue: value,
              onUpdate: onChange,
              keyPath: path,
              label: label,
              helpText: inline_help_text,
              isRequired: required,
              requiredForSaving: supportsInlinePlaceholdersAndLabels && field.required_for_saving
              // `label`, `helpText`, and `isRequired` above will only be used
              //  if `supportsInlinePlaceholdersAndLabels` is true
              ,
              supportsInlinePlaceholdersAndLabels: supportsInlinePlaceholdersAndLabels,
              moduleName: moduleName,
              onFocusCallback: onFocusCallback,
              onBlurCallback: onBlurCallback,
              registerInlineFieldRef: registerInlineFieldRef
              // TODO
              // schema={schema}
            }, inlineEditorKey);
          }
        case 'richtext':
          {
            return /*#__PURE__*/_jsx(AsyncInlineRichTextFieldEditor, {
              node: domNode,
              name: name,
              currentValue: value,
              onUpdate: onChange,
              keyPath: path,
              handleFocusWithPlaceholder: () => handleFocusInlineRichTextWithPlaceholder(name),
              getModuleSchemaById: getModuleSchemaById,
              getScpModuleInRichTextByName: getScpModuleInRichTextByName,
              getSelectedItemId: getSelectedItemId,
              moduleName: moduleName,
              updateMetaData: updateMetaData,
              onFocusCallback: onFocusCallback,
              onBlurCallback: onBlurCallback,
              enabledFeatures: enabled_features,
              registerInlineFieldRef: registerInlineFieldRef
              // TODO
              // schema={schema}
            }, inlineEditorKey);
          }
        case 'image':
          {
            return /*#__PURE__*/_jsx(ImageFieldEditor, {
              node: domNode,
              name: name,
              onUpdate: onChange,
              currentValue: value,
              keyPath: path,
              schema: field,
              moduleName: moduleName
            }, `data-token-inline-editor-${field.id}`);
          }
        case 'READ_ONLY':
          {
            return /*#__PURE__*/_jsx(ReadOnlyFieldEditor, {
              node: domNode,
              name: name,
              currentValue: value,
              keyPath: path,
              moduleName: moduleName
            }, inlineEditorKey);
          }
        default:
          break;
      }
      console.error(`No inline field editor for type ${type}`);
      return null;
    })
  });
}